import { Box, Typography } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import rachis from "../assets/newImg/hernieDiscale.png";
import SEO, { SITE_URL } from "../components/SEO";
import { SX } from "../sx";
import { C, F, R, S, maxW, trans } from "../tokens";

const TYPES = [
  {
    title: "Hernie lombaire (L4-L5, L5-S1)",
    desc: "La plus fréquente. Provoque une lombalgie irradiant dans la jambe (sciatique ou cruralgie), aggravée en position assise ou à la flexion du tronc.",
  },
  {
    title: "Hernie cervicale (C5-C6, C6-C7)",
    desc: "Douleurs dans le cou irradiant dans le bras et la main, parfois accompagnées de fourmillements ou d'une faiblesse musculaire du membre supérieur.",
  },
  {
    title: "Hernie dorsale (rare)",
    desc: "Moins courante, elle peut provoquer des douleurs intercostales ou une sensation de compression dans le milieu du dos.",
  },
];

const BIENFAITS = [
  {
    num: "01",
    title: "Réduire les tensions compressives",
    desc: "Les muscles paravertébraux en spasme aggravent la pression sur le disque. L'ostéopathie relâche ces contractures pour diminuer la compression discale.",
  },
  {
    num: "02",
    title: "Restaurer la mobilité vertébrale",
    desc: "Un segment vertébral bloqué redistribue les contraintes sur les disques adjacents. La mobilisation douce libère ces blocages et soulage l'inflammation locale.",
  },
  {
    num: "03",
    title: "Corriger les compensations posturales",
    desc: "Le corps adopte des postures antalgiques qui créent de nouvelles tensions. L'ostéopathe traite l'ensemble de la chaîne pour éviter les récidives.",
  },
  {
    num: "04",
    title: "Accompagner la résorption naturelle",
    desc: "Dans la majorité des cas, la hernie se résorbe spontanément en quelques semaines. L'ostéopathie accélère ce processus en optimisant l'environnement mécanique du disque.",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Anamnèse précise",
    desc: "Localisation, intensité, facteurs déclenchants et imagerie disponible (IRM, scanner) pour établir le bilan complet avant toute manipulation.",
  },
  {
    num: "02",
    title: "Évaluation neurologique",
    desc: "Tests de compression, de sensibilité et de force musculaire pour mesurer le degré d'irritation nerveuse et définir les techniques adaptées.",
  },
  {
    num: "03",
    title: "Techniques douces adaptées",
    desc: "Mobilisations passives, techniques myofasciales et travail viscéral  sans manipulation à haute vélocité sur les zones aiguës.",
  },
  {
    num: "04",
    title: "Conseils & prévention",
    desc: "Postures de décharge, renforcement du gainage, ergonomie au travail : des habitudes concrètes pour prévenir la rechute.",
  },
];

const HernieDiscale = () => (
  <>
    <SEO
      title="Hernie discale & ostéopathie  Balma & Castelnau"
      description="L'ostéopathie peut soulager efficacement les douleurs liées à une hernie discale lombaire ou cervicale. Maxime Nicolaieff, ostéopathe D.O. à Balma, adapte chaque séance à la sévérité de la hernie."
      path="/pathologies/hernie-discale"
      schema={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Pathologies",
            item: `${SITE_URL}/pathologies`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Hernie discale",
            item: `${SITE_URL}/pathologies/hernie-discale`,
          },
        ],
      }}
    />

    {/* ── Hero ──────────────────────────────── */}
    <Box component="section" sx={SX.pageHeroSection}>
      <Box
        sx={{
          ...SX.pageHeroInner,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 340px" },
          gap: { xs: "2rem", md: "4rem" },
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Rachis
          </Box>
          <Typography variant="h1" component="h1" sx={SX.displayTitle}>
            Hernie discale <em>soulager sans chirurgie.</em>
          </Typography>
          <Typography sx={SX.pageHeroSub}>
            90 % des hernies discales se résolvent naturellement. L'ostéopathie
            accélère ce processus en réduisant les tensions mécaniques autour du
            disque et en soulageant la douleur dès les premières séances.
          </Typography>
          <Box
            sx={{ display: "flex", gap: "1rem", flexWrap: "wrap", mt: "2rem" }}
          >
            <Box
              component="a"
              href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
              target="_blank"
              rel="noreferrer"
              sx={SX.btnDark}
            >
              Prendre rendez-vous
            </Box>
            <Box component={Link} to="/pathologies" sx={SX.btnOutline}>
              Toutes les pathologies
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: { xs: "220px", md: "320px" },
            borderRadius: R.lg,
            overflow: "hidden",
            boxShadow: S.md,
            display: { xs: "none", md: "block" },
          }}
        >
          <Box
            component="img"
            src={rachis}
            alt="Hernie discale  traitement ostéopathique"
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      </Box>
    </Box>

    {/* ── Types de hernies ──────────────────── */}
    <Box component="section" sx={{ py: { xs: "3rem", md: "5rem" } }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Comprendre
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2.5rem" }}
        >
          Toutes les hernies <em>ne se ressemblent pas.</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: "1.25rem",
          }}
        >
          {TYPES.map((t) => (
            <Box
              key={t.title}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.5rem",
                borderTop: `3px solid ${C.accent}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: ".9rem",
                  fontWeight: 600,
                  color: C.text,
                  mb: ".6rem",
                }}
              >
                {t.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: ".82rem",
                  color: C.textMuted,
                  lineHeight: 1.65,
                }}
              >
                {t.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Comment l'ostéopathie aide ────────── */}
    <Box
      component="section"
      sx={{ py: { xs: "3rem", md: "5.5rem" }, background: C.bgAlt }}
    >
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Traitement
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2.5rem" }}
        >
          Comment l'ostéopathie <em>aide concrètement ?</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "1.5rem",
          }}
        >
          {BIENFAITS.map((b) => (
            <Box
              key={b.num}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.75rem",
                display: "grid",
                gridTemplateColumns: "40px 1fr",
                gap: "1rem",
                alignItems: "start",
                transition: `box-shadow ${trans}`,
                "&:hover": { boxShadow: S.sm },
              }}
            >
              <Typography
                sx={{
                  fontFamily: F.display,
                  fontSize: "1.6rem",
                  fontWeight: 300,
                  color: C.accent,
                  lineHeight: 1,
                }}
              >
                {b.num}
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontSize: ".92rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".4rem",
                  }}
                >
                  {b.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".82rem",
                    color: C.textMuted,
                    lineHeight: 1.65,
                  }}
                >
                  {b.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Déroulement ───────────────────────── */}
    <Box component="section" sx={{ py: { xs: "3rem", md: "5.5rem" } }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
          gap: { xs: "3rem", md: "5rem" },
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Déroulement
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "1rem" }}
          >
            Une approche <em>progressive et sûre.</em>
          </Typography>
          <Typography
            sx={{ fontSize: ".85rem", color: C.textMuted, lineHeight: 1.75 }}
          >
            La sécurité prime. L'intensité du traitement est ajustée à chaque
            séance en fonction de votre tolérance et de l'évolution de vos
            symptômes.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {STEPS.map((s, i) => (
            <Box
              key={s.num}
              sx={{
                display: "grid",
                gridTemplateColumns: "48px 1fr",
                gap: "1.25rem",
                alignItems: "start",
                py: "1.5rem",
                borderBottom:
                  i < STEPS.length - 1 ? `1px solid ${C.border}` : "none",
              }}
            >
              <Typography
                sx={{
                  fontFamily: F.display,
                  fontSize: "1.8rem",
                  fontWeight: 300,
                  color: C.accent,
                  lineHeight: 1,
                  pt: ".1rem",
                }}
              >
                {s.num}
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontSize: ".92rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".3rem",
                  }}
                >
                  {s.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".82rem",
                    color: C.textMuted,
                    lineHeight: 1.7,
                  }}
                >
                  {s.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Attention médicale ────────────────── */}
    <Box component="section" sx={{ py: { xs: "1.5rem", md: "2rem" } }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box
          sx={{
            display: "flex",
            gap: ".75rem",
            alignItems: "flex-start",
            background: "#fff8f0",
            border: "1px solid #f0d0b0",
            borderRadius: R.base,
            px: "1.25rem",
            py: "1rem",
            fontSize: ".82rem",
            color: C.textMuted,
            lineHeight: 1.7,
          }}
        >
          <Box
            component="span"
            sx={{ fontSize: "1rem", flexShrink: 0, mt: ".05rem" }}
          >
            ⚠️
          </Box>
          <Box>
            Une imagerie (IRM) est fortement recommandée avant la prise en
            charge ostéopathique. Consultez un médecin en urgence si vous
            présentez une paralysie progressive, une perte de contrôle
            sphinctérienne ou une douleur d'apparition brutale après un
            traumatisme.{" "}
            <strong>
              Ces signes peuvent indiquer une hernie extrudée nécessitant une
              intervention chirurgicale.
            </strong>
          </Box>
        </Box>
      </Box>
    </Box>

    {/* ── CTA ───────────────────────────────── */}
    <Box
      component="section"
      sx={{
        background: C.dark,
        py: { xs: "3.5rem", md: "6rem" },
        mt: { xs: "2rem", md: "3rem" },
      }}
    >
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          gap: "3rem",
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabelWhite}>
            Prendre rendez-vous
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: F.display,
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1.2,
              mb: ".75rem",
              "& em": { fontStyle: "italic", color: "rgba(255,255,255,.6)" },
            }}
          >
            Hernie discale diagnostiquée ?<br />
            <em>Évitez d'attendre.</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".85rem",
              color: "rgba(255,255,255,.55)",
              maxWidth: "420px",
              lineHeight: 1.75,
              mb: "1.5rem",
            }}
          >
            Plus la prise en charge est précoce, plus les résultats sont
            durables. Apportez votre IRM ou scanner si vous en avez un.
          </Typography>
          <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <Box
              component="a"
              href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
              target="_blank"
              rel="noreferrer"
              sx={SX.btnAccent}
            >
              Réserver une séance
            </Box>
            <Box component="a" href="tel:0695788983" sx={SX.btnOutlineWhite}>
              06 95 78 89 83
            </Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
          <Typography
            sx={{
              fontFamily: F.display,
              fontSize: "4.5rem",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1,
            }}
          >
            90
            <Box
              component="span"
              sx={{ fontSize: "2rem", verticalAlign: "middle", ml: ".2rem" }}
            >
              %
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: ".72rem",
              color: "rgba(255,255,255,.4)",
              mt: ".6rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            des hernies
            <br />
            se résolvent sans chirurgie
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* ── Liens internes ────────────────────── */}
    <Box
      sx={{
        maxWidth: maxW,
        mx: "auto",
        px: { xs: "1rem", md: "2.5rem" },
        py: { xs: "2.5rem", md: "3rem" },
        display: "flex",
        gap: "1rem",
        flexWrap: "wrap",
      }}
    >
      <Box
        component={Link}
        to="/pathologies/sciatique"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Sciatique →
      </Box>
      <Box
        component={Link}
        to="/pathologies/tendinite"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Tendinites →
      </Box>
      <Box
        component={Link}
        to="/pathologies"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Toutes les pathologies →
      </Box>
    </Box>
  </>
);

export default HernieDiscale;
